import { ChevronLeftBlueIcon, ChevronRightBlueIcon } from "@/pages/taskpane/components/icons/IconCustom";
import Button from "@/pages/taskpane/components/ui/Button";
import { useSettingStore, useThemeStore } from "@/pages/taskpane/shared/stores";
import HStack from "@components/layout/HStack";
import React, { useEffect, useMemo, useState } from "react";
import { EBusyStatus, EStatusResource } from "../../resources/services/types/enum";
import {
  addDays,
  calculateClosedHoursBlocks,
  calculatePosition,
  generateHourLabels,
  getViewFromHour,
  HOURS_CHANGE_VIEW,
  HOURS_PER_VIEW,
  isOverlap,
  isSameDay,
  parseISOString,
  timeToMinutes,
  TOTAL_VIEWS,
} from "../../resources/ulti";
import { useFormatDateTime } from "@/pages/taskpane/shared/hooks";

// Types
export interface Meeting {
  id: string;
  title: string;
  startTime: string; // ISO format with timezone
  endTime: string;
  status: EBusyStatus;
}

interface TimelineProps {
  selectedStartTime: string; // ISO string - appointment start time
  selectedEndTime: string; // ISO string - appointment end time
  meetings?: Meeting[];
  currentDate?: string; // ISO string
  onDateChange?: (newDate: string) => void; // Callback when display date changes
  attendeeStatus?: EStatusResource;
  openStartTime: string | null; // ISO string - resource opening start time (empty if not set)
  openEndTime: string | null; // ISO string - resource opening end time (empty if not set)
}

export interface ITimeBlock {
  id: string;
  position: { left: string; width: string } | null;
  type: "selected" | "meeting";
  hasOverlap: boolean;
  title?: string;
  status?: EBusyStatus;
}

const Timeline = ({
  selectedStartTime = "2025-11-11T07:30:00+01:00",
  selectedEndTime = "2025-11-11T09:00:00+01:00",
  meetings = [],
  currentDate = new Date().toISOString(),
  onDateChange,
  attendeeStatus,
  openStartTime = "",
  openEndTime = "",
}: TimelineProps) => {
  const { timeFormat } = useSettingStore();
  const { isDarkMode } = useThemeStore();
  const { handleFormatDateWithSeparator, handleFormatTimeWithLanguage } = useFormatDateTime();
  const [displayDate, setDisplayDate] = useState(() => parseISOString(currentDate));
  const [currentView, setCurrentView] = useState(() => {
    // Use selectedStartTime to determine initial view
    const selectedStartParsed = parseISOString(selectedStartTime);
    return getViewFromHour(selectedStartParsed.hours);
  });

  // Update displayDate when currentDate prop changes
  useEffect(() => {
    const newDate = parseISOString(currentDate);
    setDisplayDate(newDate);
    // console.log("📅 Timeline: Updated displayDate from prop", { currentDate, newDate });
  }, [currentDate]);

  // Calculate time ranges for current view
  const viewTimeRange = useMemo(() => {
    const startHour = currentView * HOURS_CHANGE_VIEW;
    const endHour = startHour + HOURS_PER_VIEW;
    return { startHour, endHour };
  }, [currentView]);

  // Check if selected time overlaps with any meeting
  const selectedHasOverlap = useMemo(() => {
    if (attendeeStatus === EStatusResource.Unavailable) {
      return true;
    }
    const selectedStartParsed = parseISOString(selectedStartTime);
    const selectedEndParsed = parseISOString(selectedEndTime);
    const selectedStart = timeToMinutes(selectedStartParsed);
    const selectedEnd = timeToMinutes(selectedEndParsed);

    return meetings.some((meeting) => {
      const meetingStartParsed = parseISOString(meeting.startTime);
      const meetingEndParsed = parseISOString(meeting.endTime);

      if (!isSameDay(meetingStartParsed, displayDate)) return false;

      const mStart = timeToMinutes(meetingStartParsed);
      const mEnd = timeToMinutes(meetingEndParsed);

      return isOverlap(selectedStart, selectedEnd, mStart, mEnd);
    });
  }, [selectedStartTime, selectedEndTime, meetings, displayDate, attendeeStatus]);

  // Get all time blocks to display
  const timeBlocks = useMemo(() => {
    const blocks: Array<ITimeBlock> = [];

    const selectedStartParsed = parseISOString(selectedStartTime);
    const selectedEndParsed = parseISOString(selectedEndTime);

    // Add other meetings FIRST (render phía dưới)
    meetings.forEach((meeting) => {
      const meetingStartParsed = parseISOString(meeting.startTime);
      const meetingEndParsed = parseISOString(meeting.endTime);

      const position = calculatePosition(
        meetingStartParsed,
        meetingEndParsed,
        displayDate,
        viewTimeRange.startHour,
        viewTimeRange.endHour
      );
      const startTimeTitle = handleFormatTimeWithLanguage(meeting.startTime)
      const endTimeTitle = handleFormatTimeWithLanguage(meeting.endTime);

      if (position) {
        blocks.push({
          id: meeting.id,
          position,
          type: "meeting",
          hasOverlap: false,
          title: `${startTimeTitle || ""} - ${endTimeTitle || ""}`,
          status: meeting.status,
        });
      }
    });

    // Add selected time LAST (render phía trên, đè lên meetings)
    const selectedPosition = calculatePosition(
      selectedStartParsed,
      selectedEndParsed,
      displayDate,
      viewTimeRange.startHour,
      viewTimeRange.endHour
    );
    if (selectedPosition) {
      const startTimeISO = `${selectedStartParsed.year}-${String(selectedStartParsed.month).padStart(2, "0")}-${String(selectedStartParsed.day).padStart(2, "0")}T${String(selectedStartParsed.hours).padStart(2, "0")}:${String(selectedStartParsed.minutes).padStart(2, "0")}:${String(selectedStartParsed.seconds).padStart(2, "0")}`;
      const endTimeISO = `${selectedEndParsed.year}-${String(selectedEndParsed.month).padStart(2, "0")}-${String(selectedEndParsed.day).padStart(2, "0")}T${String(selectedEndParsed.hours).padStart(2, "0")}:${String(selectedEndParsed.minutes).padStart(2, "0")}:${String(selectedEndParsed.seconds).padStart(2, "0")}`;
      const startTimeTitle = handleFormatTimeWithLanguage(startTimeISO);
      const endTimeTitle = handleFormatTimeWithLanguage(endTimeISO);
      blocks.push({
        id: "selected",
        position: selectedPosition,
        type: "selected",
        hasOverlap: selectedHasOverlap,
        title: `${startTimeTitle || ""} - ${endTimeTitle || ""}`,
      });
    }

    return blocks;
  }, [selectedStartTime, selectedEndTime, meetings, currentView, displayDate, selectedHasOverlap]);

  // Navigation handlers with auto day switching
  const handlePrevView = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (currentView > 0) {
      setCurrentView(currentView - 1);
    } else {
      // At view 0, scroll left → go to previous day, last view (20-24h)
      const newDate = addDays(displayDate, -1);
      setDisplayDate(newDate);
      setCurrentView(TOTAL_VIEWS - 1); // Go to last view (20-24h)

      // Notify parent component about date change
      if (onDateChange) {
        const isoString = `${newDate.year}-${String(newDate.month).padStart(2, "0")}-${String(newDate.day).padStart(
          2,
          "0"
        )}`;
        onDateChange(isoString);
      }
    }
  };

  const handleNextView = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (currentView < TOTAL_VIEWS - 1) {
      setCurrentView(currentView + 1);
    } else {
      // At last view (20-24h), scroll right → go to next day, first view (0-4h)
      const newDate = addDays(displayDate, 1);
      setDisplayDate(newDate);
      setCurrentView(0); // Go to first view (0-4h)

      // Notify parent component about date change
      if (onDateChange) {
        const isoString = `${newDate.year}-${String(newDate.month).padStart(2, "0")}-${String(newDate.day).padStart(
          2,
          "0"
        )}`;
        onDateChange(isoString);
      }
    }
  };

  const closedHoursBlocks = useMemo(() => {
    if (!openStartTime || !openEndTime) {
      return []; // No opening hours, no closed hours bars
    }

    try {
      const openStartParsed = parseISOString(openStartTime);
      const openEndParsed = parseISOString(openEndTime);

      // Convert opening hours to minutes since midnight
      const openStartMinutes = timeToMinutes(openStartParsed);
      const openEndMinutes = timeToMinutes(openEndParsed);

      return calculateClosedHoursBlocks(
        openStartMinutes,
        openEndMinutes,
        displayDate,
        viewTimeRange.startHour,
        viewTimeRange.endHour
      );
    } catch (error) {
      return [];
    }
  }, [openStartTime, openEndTime, displayDate, viewTimeRange]);

  // Generate hour labels for current view
  const hourLabels = useMemo(() => {
    return generateHourLabels(viewTimeRange.startHour, timeFormat);
  }, [viewTimeRange]);

  const renderStripePattern = (timeBlock: ITimeBlock): React.CSSProperties => {
    switch (timeBlock.status) {
      case EBusyStatus.Free:
        return {
          backgroundColor: isDarkMode ? "#242424" : "#ffffff",
          borderColor: isDarkMode ? "#479ef5" : "#0f6cbd",
        };
      case EBusyStatus.Busy:
        return {
          backgroundColor: isDarkMode ? "#479ef5" : "#0f6cbd",
          borderColor: isDarkMode ? "#479ef5" : "#0f6cbd",
        };
      case EBusyStatus.WorkingElsewhere:
        const dotColor = isDarkMode ? "#479ef5" : "#0f6cbd";
        const bgColor = isDarkMode ? "#1a1a1a" : "#ffffff";
        // Tạo pattern chấm vuông xếp dọc như trong ảnh
        return {
          backgroundColor: bgColor,
          borderColor: dotColor,
          borderWidth: 1,
          backgroundImage: `radial-gradient(circle, ${dotColor} 1px, transparent 1px)`,
          backgroundSize: "3px 4px",
          backgroundPosition: "center center",
          backgroundRepeat: "repeat",
        };
      case EBusyStatus.Tentative:
        const stripeColor = isDarkMode ? "#479ef5" : "#0f6cbd";
        const stripeWhiteColor = "#ffffff";
        // Tạo pattern sọc chéo 45 độ như trong ảnh - nền xanh với sọc trắng
        return {
          backgroundColor: stripeColor,
          borderColor: stripeColor,
          backgroundImage: `repeating-linear-gradient(
            45deg,
            ${stripeColor} 0px,
            ${stripeColor} 4px,
            ${stripeWhiteColor} 4px,
            ${stripeWhiteColor} 8px
          )`,
          backgroundSize: "8px 8px",
        };
      case EBusyStatus.OutOfOffice:
        // Light: #b4009e với alpha e6 (230/255 ≈ 0.9), Dark: #f500d8 với alpha e6
        const oofColor = isDarkMode ? "rgba(245, 0, 216, 0.9)" : "rgba(180, 0, 158, 0.9)";
        const oofBorderColor = isDarkMode ? "#f500d8" : "#b4009e";
        const oofStripeColor = "#ffffff";
        // Tạo pattern dấu X nằm ngang với sọc chéo và sọc ngang
        return {
          backgroundColor: oofColor,
          borderColor: oofBorderColor,
          backgroundImage: `
            repeating-linear-gradient(
              45deg,
              ${oofColor} 0px,
              ${oofColor} 3px,
              ${oofStripeColor} 3px,
              ${oofStripeColor} 4px
            ),
            repeating-linear-gradient(
              -45deg,
              ${oofColor} 0px,
              ${oofColor} 3px,
              ${oofStripeColor} 3px,
              ${oofStripeColor} 4px
            )
          `,
          backgroundSize: "8px 8px, 8px 8px",
        };
      default:
        return {};
    }
  };

  return (
    <div
      className="w-full"
    // onClick={(e) => e.stopPropagation()}
    >
      {/* Date header with day navigation */}
      <HStack justifyContent="end" className="mb-2">
        <p className="text-[10px] text-darkerGray dark:text-darkDarkerGray font-semibold">
          {handleFormatDateWithSeparator(displayDate)}
        </p>
      </HStack>

      {/* Timeline with scroll controls */}
      <HStack className="w-full gap-[6px]">
        <Button
          leftIcon={
            <ChevronLeftBlueIcon
              isDarkMode={isDarkMode}
              size={16}
              className="bg-ultraLightGray rounded-sm hover:bg-lightGray dark:border dark:border-darkMediumGray dark:bg-darkBackgroundColor dark:hover:bg-darkMediumGray"
            />
          }
          onClick={handlePrevView}
        />

        <div className="flex-1 relative h-3 bg-white dark:bg-darkBackgroundColor border border-lightGray rounded-[2px] dark:border-darkMediumGray">
          {closedHoursBlocks.map((block, index) => (
            <div
              key={`closed-${index}`}
              className="absolute h-full bg-gray-400 dark:bg-gray-600 rounded-[2px] opacity-50"
              style={{
                left: block.left,
                width: block.width,
                zIndex: 0, // Lowest z-index, behind all other blocks
              }}
              title="Closed hours"
            />
          ))}
          {/* Render time blocks */}
          {timeBlocks.map((block) => {
            if (!block.position) return null;

            let bgColor = "";
            let borderColor = "";
            let opacity = 50;
            if (block.type === "selected") {
              if (block.hasOverlap) {
                // Selected time overlaps with meeting → red
                bgColor = "bg-red-400";
                borderColor = "border-red-600";
                opacity = 100;
              } else {
                // Selected time no overlap → green with stripes
                bgColor = "bg-primaryGreen40";
                borderColor = "border-primaryGreen";
                opacity = 100;
              }
              return (
                <div
                  key={block.id}
                  className={`absolute h-full ${bgColor} border ${borderColor} rounded-[2px] transition-all ${opacity ? `opacity-${opacity}` : ""
                    }`}
                  style={{
                    left: block.position.left,
                    width: block.position.width,
                    zIndex: 10, // Selected time luôn ở trên
                  }}
                  title={block.title}
                />
              );
            } else {
              return (
                <div
                  key={block.id}
                  className={`absolute h-full border rounded-[2px] transition-all`}
                  style={{
                    left: block.position.left,
                    width: block.position.width,
                    zIndex: 1,
                    ...renderStripePattern(block),
                  }}
                  title={block.title}
                />
              );
            }
          })}
        </div>

        <Button
          rightIcon={
            <ChevronRightBlueIcon
              isDarkMode={isDarkMode}
              size={16}
              className="bg-ultraLightGray rounded-sm hover:bg-lightGray dark:border dark:border-darkMediumGray dark:bg-darkBackgroundColor dark:hover:bg-darkMediumGray"
            />
          }
          onClick={handleNextView}
        />
      </HStack>

      {/* Hour labels */}
      <HStack justifyContent="between" className="text-[10px] text-mediumGray dark:text-midGray mt-1">
        {hourLabels.map((label, index) => (
          <span key={index}>{label}</span>
        ))}
      </HStack>
    </div>
  );
};

export default Timeline;
